import { useEffect, useRef, useState } from "react";
import { Link, Outlet } from "@tanstack/react-router";
import type { MonitoredUser, UserRole } from "@rua-watch/shared";
import { ApiError } from "../api/client";
import { AlertRulesPanel } from "../components/AlertRulesPanel";
import { Drawer, DrawerField, drawerControlClass } from "../components/Drawer";
import {
  useBackfillMonitoredDomainRoots,
  useBackfillQuarantine,
  useCleanupReputation,
  useCreateMonitoredUser,
  useDeleteMonitoredUser,
  useMe,
  useMonitoredUsers,
  useRefreshEnrichment,
  useReputationStatus,
  useRunDnsDrift,
  useRunReputationSweep,
  useUpdateMonitoredUser,
  useUploadTlsRpt,
} from "../api/hooks";
import { formatInt, fromUnix } from "../lib/format";

// Admin area. Visibility is also gated by Sidebar (link hidden for
// non-admins) but the API enforces the role independently — see
//`requireAdmin` middleware in apps/worker/src/api/auth.ts.
//
// Layout pattern: AdminLayoutRoute is the shell (role gate + sub-nav
// + <Outlet/>). Three child routes (`AdminUsersRoute`, `AdminAlertsRoute`,
//`AdminOperationsRoute`) render the actual sections. Default route at
// /admin renders Users content directly so deep-linking to `/admin`
// just lands on the most-used section.

const SECTIONS = [
  { to:"/admin/users", label:"Users" },
  { to:"/admin/alerts", label:"Alerts" },
  { to:"/admin/operations", label:"Operations" },
  { to:"/admin/usage", label:"Plan & usage" },
] as const;

export function AdminLayoutRoute() {
  const me = useMe();
  if (me.isLoading) return <p className= "text-slate-500 dark:text-slate-400">Loading…</p>;
  // Platform admins operate inside any tenant (the backend's
  // requireTenantMember("admin") short-circuits for them), so the gate must
  // honor isPlatformAdmin even when the user isn't a member of the active
  // tenant — otherwise switching to a workspace you don't belong to wrongly
  // blocks the admin pages with "Your role: platform_admin".
  const canAdmin = !!me.data && (me.data.isPlatformAdmin || me.data.roles.includes("admin"));
  if (!canAdmin) {
    return (
      <div className= "rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/30 p-4 text-sm text-amber-900 dark:text-amber-200">
        This page is restricted to admins. Your role: {me.data?.roles.join(", ") ?? "—"}.
      </div>
    );
  }

  return (
    <div className= "space-y-6">
      <div>
        <h1 className= "text-xl font-semibold text-slate-900 dark:text-slate-100">Admin</h1>
        <p className= "text-sm text-slate-500 dark:text-slate-400">
          Manage user access, alert rules, and run maintenance tasks.
        </p>
      </div>

      <nav className= "flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {SECTIONS.map((s) => (
          <Link
            key={s.to}
            to={s.to}
            className= "rounded-t-md border-b-2 border-transparent px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            activeProps={{
              className:"rounded-t-md border-b-2 border-slate-900 px-3 py-2 text-sm font-medium text-slate-900 dark:text-slate-100",
            }}
          >
            {s.label}
          </Link>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}

// Default landing for /admin — renders the same content as /admin/users
// so a click on"Admin" in the sidebar always lands somewhere useful.
export function AdminUsersRoute() {
  const me = useMe();
  const users = useMonitoredUsers();
  // null = drawer closed; { user: null } = create; { user } = edit.
  const [drawer, setDrawer] = useState<{ user: MonitoredUser | null } | null>(null);
  if (!me.data) return null;

  return (
    <div className= "space-y-6">
      <section className= "overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className= "flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-4 py-2">
          <div className= "flex items-center gap-2">
            <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Users</h2>
            <span className= "text-xs text-slate-500 dark:text-slate-400">{users.data?.length ?? 0}</span>
          </div>
          <button
            type= "button"
            onClick={() => setDrawer({ user: null })}
            className= "rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
          >
            + Add user
          </button>
        </div>
        {users.isLoading ? (
          <p className= "px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : users.error ? (
          <p className= "px-4 py-6 text-center text-sm text-red-700 dark:text-red-300">
            Failed to load users.{""}
            {users.error instanceof ApiError ?`(${users.error.status}) ` : null}
          </p>
        ) : users.data && users.data.length === 0 ? (
          <p className= "px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">No users yet.</p>
        ) : (
          <table className= "min-w-full text-sm">
            <thead className= "bg-slate-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
              <tr>
                <th className= "px-4 py-2 font-medium">Email</th>
                <th className= "px-4 py-2 font-medium">Role</th>
                <th className= "px-4 py-2 font-medium">Added</th>
                <th className= "px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className= "divide-y divide-slate-100 dark:divide-slate-800">
              {(users.data ?? []).map((u) => (
                <UserRow
                  key={u.email}
                  user={u}
                  actingEmail={me.data!.email}
                  onEdit={() => setDrawer({ user: u })}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <UserDrawer
        state={drawer}
        onClose={() => setDrawer(null)}
        existingEmails={(users.data ?? []).map((u) => u.email.toLowerCase())}
      />
    </div>
  );
}

export function AdminAlertsRoute() {
  return (
    <div className= "space-y-6">
      <AlertRulesPanel />
    </div>
  );
}

export function AdminOperationsRoute() {
  return (
    <div className= "space-y-6">
      <EnrichmentRefreshPanel />
      <DnsDriftPanel />
      <ReputationSweepPanel />
      <TlsRptUploadPanel />
      <QuarantineBackfillPanel />
      <MonitoredDomainRootsBackfillPanel />
    </div>
  );
}

function UserRow({
  user,
  actingEmail,
  onEdit,
}: {
  user: MonitoredUser;
  actingEmail: string;
  onEdit: () => void;
}) {
  const remove = useDeleteMonitoredUser();
  const [error, setError] = useState<string | null>(null);
  const isSelf = user.email.toLowerCase() === actingEmail.toLowerCase();

  const handleDelete = async () => {
    if (!confirm(`Remove ${user.email}? They lose access on the next API call.`)) return;
    setError(null);
    try {
      await remove.mutateAsync(user.email);
    } catch (err) {
      setError(formatError(err));
    }
  };

  return (
    <>
      <tr>
        <td className= "px-4 py-2 font-mono text-slate-900 dark:text-slate-100">
          {user.email}
          {isSelf ? <span className= "ml-2 text-xs text-slate-400 dark:text-slate-500">(you)</span> : null}
        </td>
        <td className= "px-4 py-2">
          <span className= "rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-600 dark:text-slate-400">
            {user.role}
          </span>
        </td>
        <td className= "px-4 py-2 text-slate-600 dark:text-slate-400">{fromUnix(user.addedAt)}</td>
        <td className= "px-4 py-2 text-right">
          <div className= "inline-flex gap-1">
            <button
              onClick={onEdit}
              className= "rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              disabled={remove.isPending || isSelf}
              title={isSelf ?"You can't remove yourself" :"Remove user"}
              className= "rounded border border-red-300 dark:border-red-900/40 px-2 py-1 text-xs text-red-700 dark:text-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent"
            >
              Remove
            </button>
          </div>
        </td>
      </tr>
      {error ? (
        <tr>
          <td colSpan={4} className= "bg-red-50 dark:bg-red-900/30 px-4 py-2 text-xs text-red-800 dark:text-red-300">
            {error}
          </td>
        </tr>
      ) : null}
    </>
  );
}

// Create/edit user in the side drawer (app-wide standard). `state.user`
// null → create (email + role); a user → edit (role only, email frozen
// since it's the row key / allowlist identity).
function UserDrawer({
  state,
  onClose,
  existingEmails,
}: {
  state: { user: MonitoredUser | null } | null;
  onClose: () => void;
  existingEmails: string[];
}) {
  const create = useCreateMonitoredUser();
  const update = useUpdateMonitoredUser();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("member");
  const [error, setError] = useState<string | null>(null);

  const editing = state?.user ?? null;
  const isEdit = !!editing;

  useEffect(() => {
    if (!state) return;
    setEmail(editing?.email ?? "");
    setRole(editing?.role ?? "member");
    setError(null);
  }, [state]);

  if (!state) return null;

  const submit = async () => {
    setError(null);
    try {
      if (isEdit) {
        if (role !== editing!.role) {
          await update.mutateAsync({ email: editing!.email, input: { role } });
        }
      } else {
        const normalized = email.trim().toLowerCase();
        if (!normalized) {
          setError("Email is required.");
          return;
        }
        if (existingEmails.includes(normalized)) {
          setError("That user already has access.");
          return;
        }
        await create.mutateAsync({ email: normalized, role });
      }
      onClose();
    } catch (err) {
      setError(formatError(err));
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Drawer
      open={!!state}
      onClose={onClose}
      title={isEdit ? `Edit ${editing!.email}` : "Add user"}
      description={isEdit ? "Change this member's role." : "Grant a new email access to this workspace."}
      footer={
        <div className= "flex items-center justify-end gap-2">
          {error ? <span className= "mr-auto text-xs text-rose-700 dark:text-rose-400">{error}</span> : null}
          <button
            type= "button"
            onClick={onClose}
            className= "rounded border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type= "button"
            onClick={submit}
            disabled={pending || (!isEdit && !email.trim())}
            className= "rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {pending ? "Saving…" : isEdit ? "Save changes" : "Add user"}
          </button>
        </div>
      }
    >
      <div className= "space-y-4">
        <DrawerField label= "Email">
          <input
            type= "email"
            value={email}
            disabled={isEdit}
            placeholder= "user@example.com"
            onChange={(e) => setEmail(e.target.value)}
            className={`${drawerControlClass} disabled:opacity-60`}
          />
        </DrawerField>
        <DrawerField label= "Role">
          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} className={drawerControlClass}>
            <option value= "member">member</option>
            <option value= "admin">admin</option>
            <option value= "owner">owner</option>
          </select>
        </DrawerField>
      </div>
    </Drawer>
  );
}

function EnrichmentRefreshPanel() {
  const refresh = useRefreshEnrichment();
  const [error, setError] = useState<string | null>(null);

  const trigger = async (mode:"stale" |"all") => {
    setError(null);
    try {
      await refresh.mutateAsync(mode);
    } catch (err) {
      setError(formatError(err));
    }
  };

  const data = refresh.data;

  return (
    <section className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div>
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Enrichment refresh</h2>
        <p className= "text-xs text-slate-500 dark:text-slate-400">
          Re-runs PTR + ASN/country lookups for IPs in <code>ip_enrichment</code>{""}
          and re-applies <code>classification_rules</code>. The work is async — clicking
          schedules messages on the <code>ips-to-enrich</code> queue, which the worker
          drains in the background. Use after rotating the ipinfo token, adding
          classification rules, or whenever the data looks stale.
        </p>
      </div>

      <div className= "flex flex-wrap gap-2">
        <button
          onClick={() => trigger("stale")}
          disabled={refresh.isPending}
          className= "rounded border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:bg-slate-200"
        >
          {refresh.isPending && refresh.variables === "stale"
            ?"Scheduling…"
            :"Refresh stale (>7d) "}
        </button>
        <button
          onClick={() => trigger("all")}
          disabled={refresh.isPending}
          className= "rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
        >
          {refresh.isPending && refresh.variables === "all"
            ?"Scheduling…"
            :"Refresh all (force) "}
        </button>
      </div>

      {data ? (
        <div className= "rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-sm text-emerald-900 dark:text-emerald-200">
          Scheduled <span className= "font-semibold">{formatInt(data.enqueued)}</span> IPs
          for re-enrichment (mode <code>{data.mode}</code>, limit {formatInt(data.limit)}).
          {data.message ? <span className= "ml-1 text-emerald-700 dark:text-emerald-300">{data.message}</span> : null}
          <p className= "mt-1 text-xs text-emerald-800 dark:text-emerald-300">
            Workers will drain the queue in the background — check counts again in a
            minute or two.
          </p>
        </div>
      ) : null}

      {error ? (
        <div className= "rounded-md border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      ) : null}
    </section>
  );
}

function DnsDriftPanel() {
  const run = useRunDnsDrift();
  const [domain, setDomain] = useState("");
  const [error, setError] = useState<string | null>(null);

  const trigger = async (target: string | undefined) => {
    setError(null);
    try {
      await run.mutateAsync(target);
    } catch (err) {
      setError(formatError(err));
    }
  };

  const data = run.data;

  return (
    <section className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div>
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">DNS configuration drift</h2>
        <p className= "text-xs text-slate-500 dark:text-slate-400">
          Snapshots SPF/DMARC/DKIM for the monitored domain(s) right now and
          records any change against the previous snapshot. The daily cron
          runs automatically — use this to seed the timeline or to capture
          a DNS change you just published without waiting.
        </p>
      </div>

      <div className= "flex flex-wrap items-end gap-2">
        <label className= "flex-1 min-w-[12rem]">
          <span className= "text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Domain (optional)</span>
          <input
            type= "text"
            placeholder= "leave blank to scan all enabled domains"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className= "mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm shadow-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
          />
        </label>
        <button
          onClick={() => trigger(domain.trim() || undefined)}
          disabled={run.isPending}
          className= "rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
        >
          {run.isPending ?"Running…" :"Run snapshot"}
        </button>
      </div>

      {data ? (
        <div className= "rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-sm text-emerald-900 dark:text-emerald-200">
          Scanned <span className= "font-semibold">{formatInt(data.domainsScanned)}</span>{""}
          domain(s) — {formatInt(data.snapshotsInserted)} snapshot row(s),{""}
          <span className= "font-semibold">{formatInt(data.changesDetected)}</span>{""}
          change(s) detected.
          {data.errors.length > 0 ? (
            <ul className= "mt-1 text-xs text-amber-800 dark:text-amber-300">
              {data.errors.map((e) => (
                <li key={e.domain}>
                  {e.domain}: {e.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className= "rounded-md border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      ) : null}
    </section>
  );
}

function ReputationSweepPanel() {
  const status = useReputationStatus();
  const sweep = useRunReputationSweep();
  const cleanup = useCleanupReputation();
  const [limit, setLimit] = useState(50);
  const [error, setError] = useState<string | null>(null);
  const [cleanupResult, setCleanupResult] = useState<{ deleted: number } | null>(null);

  const trigger = async () => {
    setError(null);
    try {
      await sweep.mutateAsync(limit);
    } catch (err) {
      setError(formatError(err));
    }
  };

  const triggerCleanup = async () => {
    setError(null);
    setCleanupResult(null);
    if (!confirm("Delete all ip_reputation rows where the return code is a DNSBL error sentinel (127.255.255.x)? This cannot be undone.")) {
      return;
    }
    try {
      const r = await cleanup.mutateAsync();
      setCleanupResult(r);
    } catch (err) {
      setError(formatError(err));
    }
  };

  const providers = status.data?.providers ?? [];
  const noProviders = !status.isLoading && providers.length === 0;

  return (
    <section className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div>
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">IP reputation sweep (DNSBL)</h2>
        <p className= "text-xs text-slate-500 dark:text-slate-400">
          Picks the top-N source IPs by 7d volume and queries each against
          every configured DNSBL. Results land in <code>ip_reputation</code>{""}
          and surface as a"Blocklisted" pill in the Sources table plus a
          Reputation card on the IP detail page. The daily cron runs the
          same sweep — use this to seed the table immediately or to rerun
          after adding a provider.
        </p>
      </div>

      {noProviders ? (
        <div className= "rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/30 p-3 text-sm text-amber-900 dark:text-amber-200">
          <p className= "font-medium">No DNSBL providers configured.</p>
          <p className= "mt-1 text-xs">
            Spamhaus blocks queries from public DoH resolvers (1.1.1.1
            included), so the free zone <code>zen.spamhaus.org</code> won't
            work from this Worker. Register a Spamhaus DQS key and set it as
            a secret to enable the sweep:
          </p>
          <pre className= "mt-2 overflow-x-auto rounded bg-amber-100 dark:bg-amber-900/30 p-2 text-xs text-amber-900 dark:text-amber-200">
            wrangler secret put SPAMHAUS_DQS_KEY
          </pre>
        </div>
      ) : status.isLoading ? null : (
        <div className= "rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 p-3 text-xs text-slate-700 dark:text-slate-300">
          <span className= "font-medium">Configured providers:</span>{""}
          {providers.map((p) => p.label).join(", ")}
        </div>
      )}

      <div className= "flex flex-wrap items-end gap-2">
        <label>
          <span className= "text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Top IPs to check (1–500)
          </span>
          <input
            type= "number"
            min={1}
            max={500}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            className= "mt-1 block w-32 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm shadow-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
          />
        </label>
        <button
          onClick={trigger}
          disabled={sweep.isPending || noProviders}
          className= "rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
        >
          {sweep.isPending ?"Running…" :"Run sweep"}
        </button>
        <button
          onClick={triggerCleanup}
          disabled={cleanup.isPending}
          title= "Remove rows where the stored return_code is 127.255.255.x — pre-safeguard sweep wrote these as listed when Spamhaus blocked query."
          className= "rounded border border-red-300 dark:border-red-900/40 px-3 py-2 text-sm text-red-700 dark:text-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent"
        >
          {cleanup.isPending ?"Cleaning…" :"Cleanup error rows"}
        </button>
      </div>

      {sweep.data ? (
        <div className= "rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-sm text-emerald-900 dark:text-emerald-200">
          Checked <span className= "font-semibold">{formatInt(sweep.data.scanned)}</span> IP(s) across{""}
          {formatInt(sweep.data.providers)} provider(s) —{""}
          <span className= "font-semibold">{formatInt(sweep.data.listed)}</span> listed,{""}
          {formatInt(sweep.data.errors)} error(s).
        </div>
      ) : null}

      {cleanupResult ? (
        <div className= "rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-sm text-emerald-900 dark:text-emerald-200">
          Removed <span className= "font-semibold">{formatInt(cleanupResult.deleted)}</span> error row(s).
        </div>
      ) : null}

      {error ? (
        <div className= "rounded-md border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      ) : null}
    </section>
  );
}

function MonitoredDomainRootsBackfillPanel() {
  const backfill = useBackfillMonitoredDomainRoots();
  const [error, setError] = useState<string | null>(null);

  const trigger = async () => {
    setError(null);
    try {
      await backfill.mutateAsync();
    } catch (err) {
      setError(formatError(err));
    }
  };

  const data = backfill.data;

  return (
    <section className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div>
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Monitored domains: backfill org roots</h2>
        <p className= "text-xs text-slate-500 dark:text-slate-400">
          Populates <code>root_domain</code> on legacy <code>monitored_domains</code>{""}
          rows added before migration 0006. Subdomains like{""}
          <code>cc.plusdin.com.br</code> get rolled up under{""}
          <code>plusdin.com.br</code> in the listing, and missing root entries are
          auto-created. Idempotent — safe to run multiple times.
        </p>
      </div>

      <div>
        <button
          onClick={trigger}
          disabled={backfill.isPending}
          className= "rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
        >
          {backfill.isPending ?"Running…" :"Backfill root_domain"}
        </button>
      </div>

      {data ? (
        <div className= "rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-sm text-emerald-900 dark:text-emerald-200">
          Scanned <span className= "font-semibold">{formatInt(data.scanned)}</span> legacy row(s) —{""}
          <span className= "font-semibold">{formatInt(data.updated)}</span> updated,{""}
          {formatInt(data.rootsCreated)} root domain(s) auto-created.
          {data.scanned === 0 ? (
            <p className= "mt-1 text-xs text-emerald-800 dark:text-emerald-300">
              Nothing left to backfill — every monitored domain has a root.
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className= "rounded-md border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      ) : null}
    </section>
  );
}

function QuarantineBackfillPanel() {
  const backfill = useBackfillQuarantine();
  const [limit, setLimit] = useState(100);
  const [error, setError] = useState<string | null>(null);

  const trigger = async () => {
    setError(null);
    try {
      await backfill.mutateAsync(limit);
    } catch (err) {
      setError(formatError(err));
    }
  };

  const data = backfill.data;

  return (
    <section className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div>
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Quarantine metadata backfill</h2>
        <p className= "text-xs text-slate-500 dark:text-slate-400">
          Recovers <code>sender_from</code>, <code>subject</code>,{""}
          <code>filename</code> and <code>size_bytes</code> for quarantine entries
          created before migration 0005. The backfill never overwrites existing
          values — it only fills NULL columns. Read-only against R2 (HEAD + body
          for header extraction); D1 writes only the diff. Run repeatedly with
          increasing <code>limit</code> until <code>scanned</code> drops to zero.
        </p>
      </div>

      <div className= "flex flex-wrap items-end gap-2">
        <label>
          <span className= "text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Limit per call (1–500)
          </span>
          <input
            type= "number"
            min={1}
            max={500}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            className= "mt-1 block w-32 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm shadow-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
          />
        </label>
        <button
          onClick={trigger}
          disabled={backfill.isPending}
          className= "rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
        >
          {backfill.isPending ?"Running…" :"Run backfill"}
        </button>
      </div>

      {data ? (
        <div className= "rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-sm text-emerald-900 dark:text-emerald-200">
          Scanned <span className= "font-semibold">{formatInt(data.scanned)}</span> row(s) —{""}
          <span className= "font-semibold">{formatInt(data.recovered)}</span> recovered{""}
          ({formatInt(data.fullyRecovered)} fully, {formatInt(data.unrecoverable)} unrecoverable).
          {data.errors.length > 0 ? (
            <ul className= "mt-1 text-xs text-amber-800 dark:text-amber-300">
              {data.errors.slice(0, 5).map((e) => (
                <li key={e.id}>
                  #{e.id}: {e.message}
                </li>
              ))}
              {data.errors.length > 5 ? (
                <li>…and {data.errors.length - 5} more</li>
              ) : null}
            </ul>
          ) : null}
          {data.scanned === 0 ? (
            <p className= "mt-1 text-xs text-emerald-800 dark:text-emerald-300">
              No rows still need backfilling — all entries have full metadata.
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className= "rounded-md border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      ) : null}
    </section>
  );
}

// F10 — Manual TLS-RPT upload. Validates the parser + insert path
// without waiting on real SMTP. Mirrors the DMARC report upload UX
// available on /preview, but kept admin-only because it writes to
// production tables. JSON-only; gzip support exists in the parser
// but adds little value for hand-crafted samples and would need a
// content-type heuristic in the form.
function TlsRptUploadPanel() {
  const upload = useUploadTlsRpt();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const outcome = upload.data;

  const onPick = async (file: File | null) => {
    setError(null);
    if (!file) return;
    try {
      await upload.mutateAsync(file);
    } catch (err) {
      setError(formatError(err));
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <section className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div>
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">TLS-RPT manual upload</h2>
        <p className= "text-xs text-slate-500 dark:text-slate-400">
          Upload a TLS-RPT JSON report (RFC 8460) to exercise the same
          ingest path the SMTP pipeline uses. Useful for validating the
          parser, the <code>tls_reports</code> insert and the per-domain
          summary card without needing a real receiver to send a report.
          Gzip envelopes are accepted; the parser sniffs magic bytes
          before trusting the filename.
        </p>
      </div>

      <div className= "flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type= "file"
          accept= ".json,.gz,application/json,application/gzip"
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          disabled={upload.isPending}
          className= "block text-sm text-slate-700 dark:text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-slate-800 disabled:opacity-60"
        />
        {upload.isPending ? (
          <span className= "text-xs text-slate-500 dark:text-slate-400">Uploading…</span>
        ) : null}
      </div>

      {outcome && outcome.status === "ingested" ? (
        <div className= "rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-sm text-emerald-900 dark:text-emerald-200">
          Ingested as report <span className= "font-mono">#{outcome.reportRowId}</span> for{""}
          <span className= "font-mono">{outcome.policyDomain || "(unknown domain)"}</span>.
          {outcome.policyDomain ? (
            <>
              {""}
              <Link
                to= "/domains/$domain"
                params={{ domain: outcome.policyDomain }}
                className= "ml-1 underline underline-offset-2"
              >
                Open domain →
              </Link>
            </>
          ) : null}
        </div>
      ) : null}

      {outcome && outcome.status === "duplicate" ? (
        <div className= "rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 p-3 text-sm text-slate-700 dark:text-slate-300">
          Duplicate — an existing row already covers this report's{""}
          <code>(report_id, org, window) </code>combo. Stored at{""}
          <code>{outcome.r2Key}</code>.
        </div>
      ) : null}

      {outcome && outcome.status === "quarantined" ? (
        <div className= "rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/30 p-3 text-sm text-amber-900 dark:text-amber-200">
          Quarantined: <code>{outcome.reason}</code>
          {outcome.detail ? <> — {outcome.detail}</> : null}.
          <span className= "ml-1 text-xs text-amber-800 dark:text-amber-300">
            Raw bytes preserved at <code>{outcome.r2Key}</code>.
          </span>
        </div>
      ) : null}

      {error ? (
        <div className= "rounded-md border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      ) : null}
    </section>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; hint?: string } | undefined;
    const code = body?.error ?? err.message;
    return body?.hint ?`${code} — ${body.hint}` : code;
  }
  return (err as Error).message ?? "Unknown error";
}
